import type { Card, Suit } from './card';
import type { ClientGameState } from './game';
import type { PlayerId, PlayerPosition } from './player';
import type { AnimationEvent, AnimationSequenceId, CardIndexInBatch } from './animation';

// ================== Client -> Server ==================

export enum ClientMessageType {
  JOIN_ROOM = 'join_room',
  LEAVE_ROOM = 'leave_room',
  START_GAME = 'start_game',
  START_DEALING = 'start_dealing',
  SELECT_TRUMP = 'select_trump',
  PLAY_CARD = 'play_card',
  DISCARD_CARDS = 'discard_cards', // 2-player: discard selected cards
  ADD_AI = 'add_ai',
  REMOVE_AI = 'remove_ai',
  REPLACE_WITH_AI = 'replace_with_ai', // Replace disconnected player with AI
  REMOVE_PLAYER = 'remove_player', // Kick a player (host only)
  PING = 'ping',
  ANIMATION_ACK = 'animation_ack', // Acknowledge animation completion
}

export interface JoinRoomMessage {
  type: ClientMessageType.JOIN_ROOM;
  playerName: string;
  roomCode?: string;
  playerId?: string; // For rejoin: identifies the player trying to reconnect
}

export interface LeaveRoomMessage {
  type: ClientMessageType.LEAVE_ROOM;
}

export interface StartGameMessage {
  type: ClientMessageType.START_GAME;
  pointsToWin?: 3 | 7; // defaults to 7
}

export interface StartDealingMessage {
  type: ClientMessageType.START_DEALING;
}

export interface SelectTrumpMessage {
  type: ClientMessageType.SELECT_TRUMP;
  suit: Suit;
}

export interface PlayCardMessage {
  type: ClientMessageType.PLAY_CARD;
  cardId: string;
}

export interface DiscardCardsMessage {
  type: ClientMessageType.DISCARD_CARDS;
  cardIds: string[]; // Cards to discard (3 for Hakem, 2 for opponent)
}

export interface AddAIMessage {
  type: ClientMessageType.ADD_AI;
  position?: PlayerPosition;
}

export interface RemoveAIMessage {
  type: ClientMessageType.REMOVE_AI;
  playerId: string;
}

export interface PingMessage {
  type: ClientMessageType.PING;
  timestamp: number;
}

export interface ReplaceWithAIMessage {
  type: ClientMessageType.REPLACE_WITH_AI;
  playerId: string; // The disconnected player to replace
}

export interface RemovePlayerMessage {
  type: ClientMessageType.REMOVE_PLAYER;
  playerId: string; // The player to kick
}

export interface AnimationAckMessage {
  type: ClientMessageType.ANIMATION_ACK;
  event: AnimationEvent;
  sequenceId: AnimationSequenceId;
}

export type ClientMessage =
  | JoinRoomMessage
  | LeaveRoomMessage
  | StartGameMessage
  | StartDealingMessage
  | SelectTrumpMessage
  | PlayCardMessage
  | DiscardCardsMessage
  | AddAIMessage
  | RemoveAIMessage
  | ReplaceWithAIMessage
  | RemovePlayerMessage
  | PingMessage
  | AnimationAckMessage;

// ================== Server -> Client ==================

export enum ServerMessageType {
  ROOM_JOINED = 'room_joined',
  PLAYER_JOINED = 'player_joined',
  PLAYER_LEFT = 'player_left',
  PLAYER_DISCONNECTED = 'player_disconnected', // Player disconnected (still in game)
  PLAYER_REPLACED_WITH_AI = 'player_replaced_with_ai', // Disconnected player replaced with AI
  PLAYER_REMOVED = 'player_removed', // Player was kicked
  KICKED = 'kicked', // Sent to the kicked player
  GAME_STATE = 'game_state',
  HAKEM_CARD_REVEALED = 'hakem_card_revealed', // Card revealed during hakem selection
  HAKEM_SELECTED = 'hakem_selected', // Hakem determined (first Ace)
  PARTNER_SELECTED = 'partner_selected', // Partner determined (second Ace)
  SEATS_SWAPPED = 'seats_swapped', // Players swapped seats for team alignment
  CARD_DEALT = 'card_dealt', // Single card dealt during dealing phase
  CARD_PLAYED = 'card_played',
  TRUMP_SELECTED = 'trump_selected',
  DISCARD_REQUIRED = 'discard_required', // 2-player: prompt to discard cards
  CARDS_DISCARDED = 'cards_discarded', // 2-player: player discarded cards
  STOCK_DRAW = 'stock_draw', // 2-player: card drawn from stock
  TRICK_WON = 'trick_won',
  HAND_COMPLETE = 'hand_complete',
  GAME_OVER = 'game_over',
  ERROR = 'error',
  PONG = 'pong',
}

export interface RoomJoinedMessage {
  type: ServerMessageType.ROOM_JOINED;
  roomId: string;
  playerId: PlayerId;
  position: PlayerPosition;
  state: ClientGameState;
}

export interface PlayerJoinedMessage {
  type: ServerMessageType.PLAYER_JOINED;
  player: {
    id: PlayerId;
    name: string;
    position: PlayerPosition;
    isAI: boolean;
  };
}

export interface PlayerLeftMessage {
  type: ServerMessageType.PLAYER_LEFT;
  playerId: PlayerId;
  position: PlayerPosition;
}

export interface PlayerDisconnectedMessage {
  type: ServerMessageType.PLAYER_DISCONNECTED;
  playerId: PlayerId;
  playerName: string;
  position: PlayerPosition;
}

export interface PlayerReplacedWithAIMessage {
  type: ServerMessageType.PLAYER_REPLACED_WITH_AI;
  playerId: PlayerId;
  position: PlayerPosition;
  aiName: string;
}

export interface PlayerRemovedMessage {
  type: ServerMessageType.PLAYER_REMOVED;
  playerId: PlayerId;
  position: PlayerPosition;
}

export interface KickedMessage {
  type: ServerMessageType.KICKED;
  reason: string;
}

export interface GameStateMessage {
  type: ServerMessageType.GAME_STATE;
  state: ClientGameState;
}

export interface HakemCardRevealedMessage {
  type: ServerMessageType.HAKEM_CARD_REVEALED;
  card: Card;
  recipientPosition: PlayerPosition; // Who received this card
  recipientId: PlayerId;
  isAce: boolean; // True if this card makes them Hakem
  sequenceId: AnimationSequenceId;
}

export interface HakemSelectedMessage {
  type: ServerMessageType.HAKEM_SELECTED;
  hakemId: PlayerId;
  hakemPosition: PlayerPosition;
  sequenceId: AnimationSequenceId;
}

export interface PartnerSelectedMessage {
  type: ServerMessageType.PARTNER_SELECTED;
  partnerId: PlayerId;
  partnerPosition: PlayerPosition;
  hakemId: PlayerId; // Reference to hakem for UI
  sequenceId: AnimationSequenceId;
}

export interface SeatsSwappedMessage {
  type: ServerMessageType.SEATS_SWAPPED;
  swaps: Array<{
    playerId: PlayerId;
    fromPosition: PlayerPosition;
    toPosition: PlayerPosition;
  }>;
  sequenceId: AnimationSequenceId;
}

export interface CardDealtMessage {
  type: ServerMessageType.CARD_DEALT;
  card: Card; // The card dealt (face-down for others, face-up for recipient)
  recipientPosition: PlayerPosition;
  recipientId: PlayerId;
  dealingRound: number; // Which round of dealing (1, 2, 3...)
  cardIndexInBatch: CardIndexInBatch; // Position of this card within the batch (0-indexed) for client stagger timing
  isLastCardInRound: boolean; // True if this is the last card in this round
  isLastCardOverall: boolean; // True if dealing is complete
  batchSequenceId: AnimationSequenceId; // Shared by all cards in the same batch
}

export interface CardPlayedMessage {
  type: ServerMessageType.CARD_PLAYED;
  playerId: PlayerId;
  card: Card;
  position: PlayerPosition;
}

export interface TrumpSelectedMessage {
  type: ServerMessageType.TRUMP_SELECTED;
  suit: Suit;
  hakemId: PlayerId;
  sequenceId: AnimationSequenceId;
}

// 2-player specific messages
export interface DiscardRequiredMessage {
  type: ServerMessageType.DISCARD_REQUIRED;
  playerId: PlayerId;
  count: number; // How many cards to discard (3 for Hakem, 2 for opponent)
}

export interface CardsDiscardedMessage {
  type: ServerMessageType.CARDS_DISCARDED;
  playerId: PlayerId;
  count: number; // How many cards were discarded (don't reveal which ones)
}

export interface StockDrawMessage {
  type: ServerMessageType.STOCK_DRAW;
  card: Card; // The card drawn (only shown to recipient)
  recipientPosition: PlayerPosition;
  recipientId: PlayerId;
  stockRemaining: number; // Cards left in stock
}

export interface TrickWonMessage {
  type: ServerMessageType.TRICK_WON;
  winnerId: PlayerId;
  winnerPosition: PlayerPosition;
  trickCards: Card[];
  sequenceId: AnimationSequenceId;
}

// Team-based hand complete (4-player)
export interface TeamHandCompleteMessage {
  type: ServerMessageType.HAND_COMPLETE;
  mode: 'team';
  winningTeam: 'A' | 'B';
  points: number;
  isKot: boolean;
  isHakemKoti: boolean;
  newHakemId: PlayerId;
  newScore: { teamA: number; teamB: number };
  sequenceId: AnimationSequenceId;
}

// Individual hand complete (2-player, 3-player)
export interface IndividualHandCompleteMessage {
  type: ServerMessageType.HAND_COMPLETE;
  mode: 'individual';
  winnerId: PlayerId;
  points: number;
  isKot: boolean;
  isHakemKoti: boolean;
  newHakemId: PlayerId;
  newScore: { [playerId: string]: number }; // Keys are PlayerId values
  sequenceId: AnimationSequenceId;
}

export type HandCompleteMessage = TeamHandCompleteMessage | IndividualHandCompleteMessage;

// Team-based game over (4-player)
export interface TeamGameOverMessage {
  type: ServerMessageType.GAME_OVER;
  mode: 'team';
  winningTeam: 'A' | 'B';
  finalScore: { teamA: number; teamB: number };
}

// Individual game over (2-player, 3-player)
export interface IndividualGameOverMessage {
  type: ServerMessageType.GAME_OVER;
  mode: 'individual';
  winnerId: PlayerId;
  finalScore: { [playerId: string]: number }; // Keys are PlayerId values
}

export type GameOverMessage = TeamGameOverMessage | IndividualGameOverMessage;

export interface ErrorMessage {
  type: ServerMessageType.ERROR;
  code: string;
  message: string;
}

export interface PongMessage {
  type: ServerMessageType.PONG;
  timestamp: number;
  serverTime: number;
}

export type ServerMessage =
  | RoomJoinedMessage
  | PlayerJoinedMessage
  | PlayerLeftMessage
  | PlayerDisconnectedMessage
  | PlayerReplacedWithAIMessage
  | PlayerRemovedMessage
  | KickedMessage
  | GameStateMessage
  | HakemCardRevealedMessage
  | HakemSelectedMessage
  | PartnerSelectedMessage
  | SeatsSwappedMessage
  | CardDealtMessage
  | CardPlayedMessage
  | TrumpSelectedMessage
  | DiscardRequiredMessage
  | CardsDiscardedMessage
  | StockDrawMessage
  | TrickWonMessage
  | HandCompleteMessage
  | GameOverMessage
  | ErrorMessage
  | PongMessage;

// Error codes
export enum ErrorCode {
  ROOM_FULL = 'ROOM_FULL',
  ROOM_NOT_FOUND = 'ROOM_NOT_FOUND',
  NOT_YOUR_TURN = 'NOT_YOUR_TURN',
  INVALID_CARD = 'INVALID_CARD',
  MUST_FOLLOW_SUIT = 'MUST_FOLLOW_SUIT',
  GAME_NOT_STARTED = 'GAME_NOT_STARTED',
  GAME_IN_PROGRESS = 'GAME_IN_PROGRESS',
  ALREADY_IN_GAME = 'ALREADY_IN_GAME',
  NOT_HAKEM = 'NOT_HAKEM',
  INVALID_PHASE = 'INVALID_PHASE',
  PARSE_ERROR = 'PARSE_ERROR',
  INVALID_DISCARD = 'INVALID_DISCARD', // Wrong number of cards to discard
  NOT_HOST = 'NOT_HOST', // Only host can perform this action
  PLAYER_NOT_FOUND = 'PLAYER_NOT_FOUND', // Target player not found
  INVALID_ACTION = 'INVALID_ACTION', // Action not allowed in current state
  INTERNAL_ERROR = 'INTERNAL_ERROR', // Unexpected server error
}
